import { useEffect, useMemo } from "react";

import { type NodeModel } from "@/models/nodes/types";

type HandlerProps = {
  onMouseOver?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
};

type UseNodeHoverProps = {
  isHovered: boolean;
  handlerProps: HandlerProps;
};

type UseNodeHoverOptions = {
  index?: number;
  disabled?: boolean;
};

export function useNodeHover(
  node: NodeModel,
  { index, disabled }: UseNodeHoverOptions = {},
): UseNodeHoverProps {
  const { isPointerSelecting } = node.doc.selection;
  const isHovered = node.doc.nodeHover.get(node, index);

  useEffect(() => {
    return () => {
      if (node.doc.nodeHover.get(node, index)) {
        node.doc.nodeHover.reset();
      }
    };
  }, [node, index]);

  const handlerProps: HandlerProps = useMemo(() => {
    function handleOnMouseOver(e: React.MouseEvent) {
      e.stopPropagation();

      if (!node.doc.nodeHover.get(node, index)) {
        node.doc.nodeHover.set(node, index);
      }
    }

    function handleOnMouseLeave() {
      if (node.doc.nodeHover.get(node, index)) {
        node.doc.nodeHover.reset();
      }
    }

    return {
      onMouseOver: handleOnMouseOver,
      onMouseLeave: handleOnMouseLeave,
    };
  }, [node, index]);

  if (disabled || node.doc.disableEditing) {
    return {
      isHovered: false,
      handlerProps: {},
    };
  }

  return {
    isHovered: isHovered && !isPointerSelecting,
    handlerProps,
  };
}
